import { Action, Dispatch } from 'redux';
import { SelectOptionType } from '../utils/selection';
import { AssetHit } from '../api/vault/searchEngine/interfaces';
import { getAllTeams } from '../api/vault/teams';
import { getVaultSubscriptions } from '../api/vault/subscriptions';
import { IAppState } from './reducers';
import { getAssetFromApi } from '../components/AssetDetails/api/assetsLookupsMetatags';

export type TSelectOption = SelectOptionType<string>;

export enum ActionType {
  Toggle = 'ShareDialog.Toggle',
  ToggleLoading = 'ShareDialog.Loading',
  UpdateOptions = 'ShareDialog.UpdateOptions',
  ClearOptions = 'ShareDialog.ClearOptions',
  UpdateAssetDetails = 'ShareDialog.UpdateAssetDetails',
}

export interface IShareDialogAction extends Action<ActionType> {
  payload?: any;
}

export type TeamMap = { [key: string]: Array<string> };

export interface IState {
  isOpen: boolean;
  isLoading: boolean;
  selectedAssets: Array<AssetHit>;
  selectedOptions: Array<TSelectOption>;
  assetDetails: Array<any>;
  teamMap: TeamMap,
}

const INITIAL_STATE: IState = {
  isOpen: false,
  isLoading: false,
  selectedAssets: [],
  selectedOptions: [],
  assetDetails: [],
  teamMap: null,
};

export default (state: IState = INITIAL_STATE, action: IShareDialogAction) => {
  switch (action.type) {
    case ActionType.UpdateOptions:
    case ActionType.UpdateAssetDetails:
      return {
        ...state,
        ...action.payload,
      };
    case ActionType.Toggle:
      return {
        ...state,
        isOpen: !state.isOpen,
        selectedAssets: action.payload.selectedAssets,
        teamMap: action.payload.teaMap,
      };
    case ActionType.ClearOptions:
      return {
        ...state,
        selectedOptions: [],
      };
    default:
      return state;
  }
};

export const toggleShareDialog = (selectedAssets: Array<AssetHit> = []) => ({
  type: ActionType.Toggle,
  payload: {
    selectedAssets,
  },
});

export const toggleLoading = (isLoading: boolean) => ({
  type: ActionType.ToggleLoading,
  payload: {
    isLoading,
  },
});

export const updateSelectedOptions = (selectedOptions: Array<TSelectOption>) => ({
  type: ActionType.UpdateOptions,
  payload: {
    selectedOptions,
  },
});

export const clearSelectedOptions = () => ({
  type: ActionType.ClearOptions,
});

export const updateAssetDetails = (assetDetails: Array<any>) => ({
  type: ActionType.UpdateAssetDetails,
  payload: {
    assetDetails,
  },
});

export const queryAssetDetails = (selectedAssets: Array<AssetHit>) => async (
  dispatch: Dispatch<IShareDialogAction>,
  getState: () => IAppState,
) => {
  dispatch({
    type: ActionType.ToggleLoading,
    payload: {
      isLoading: true,
      assetDetails: [],
    },
  });

  const state = getState();
  const {
    securityProfiles: {
      idValueMapping: securityProfiles,
    },
  } = state;

  try {
    const { data: { data: subscriptions } } = await getVaultSubscriptions();
    const { data: { data: teams } } = await getAllTeams();
    const teamMap: TeamMap = subscriptions.reduce((source, { orgId, emailAddresses }) => {
      const securityProfile = securityProfiles.find(({ idm_org_id }) => idm_org_id === orgId);
      if (securityProfile) {
        const { team_id, _id: securityProfileId } = securityProfile;
        if (team_id) {
          const team = teams.find(({ _id }) => _id === team_id);
          if (team) {
            // eslint-disable-next-line
            source[securityProfileId] = emailAddresses;
          }
        }
      }
      return source;
    }, {} as TeamMap);

    const assetDetails = await Promise.all(selectedAssets.map((rawAsset) => getAssetFromApi(rawAsset._id)));

    dispatch({
      type: ActionType.UpdateAssetDetails,
      payload: {
        isLoading: false,
        assetDetails,
        teamMap,
      },
    });
  } catch (e) {
    dispatch({
      type: ActionType.ToggleLoading,
      payload: {
        isLoading: false,
      },
    });
  }
};
